import React, { useState, useEffect, useContext } from 'react';
import api from '../../services/api';
import { AuthContext } from '../../context/AuthContext';
import Modal from '../../comun/Modal';
import Categorias from './Categorias';
import Extras from './Extras';
import Combos from './Combos';
import Tamanos from './Tamanos';
import MovimientosInventario from './MovimientosInventario';

const Inventario = () => {
    const [activeTab, setActiveTab] = useState('products'); // products, categories, extras, combos, sizes

    // Estado para Productos
    const [products, setProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);

    // Estado del formulario de producto
    const [isProductMovementsModalOpen, setIsProductMovementsModalOpen] = useState(false);

    const [newProduct, setNewProduct] = useState({
        name: '',
        description: '',
        price: '',
        tax_percentage: 0,
        category: '',
        image: null,
        is_active: true,
        is_available: true,
        max_toppings: 0,
        contifico_id: ''
    });
    const [editingProduct, setEditingProduct] = useState(null);

    const { user } = useContext(AuthContext);
    const roleName = user?.role_details?.name;
    const isAdmin = roleName === 'SUPER_ADMIN' || roleName === 'ADMIN_HELADERIA' || user?.is_superuser;

    const fetchProducts = async () => {
        try {
            const response = await api.get('/api/menu/products/', {
                baseURL: process.env.REACT_APP_HELADERIA_SERVICE
            });
            setProducts(response.data.results || response.data || []);
        } catch (err) {
            console.error('Error fetching products:', err);
            setError('Error al cargar el inventario');
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await api.get('/api/menu/categories/', {
                baseURL: process.env.REACT_APP_HELADERIA_SERVICE
            });
            setCategories(response.data.results || response.data || []);
        } catch (err) {
            console.error('Error fetching categories:', err);
        }
    };

    useEffect(() => {
        if (activeTab === 'products') {
            fetchProducts();
            fetchCategories();
        }
    }, [activeTab]);


    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        setNewProduct(prev => ({ ...prev, [name]: newValue }));
    };

    const handleImageChange = (e) => {
        setNewProduct(prev => ({ ...prev, image: e.target.files[0] }));
    };

    const handleEditProduct = (product) => {
        setEditingProduct(product);
        setNewProduct({
            name: product.name,
            description: product.description,
            price: product.price,
            tax_percentage: product.tax_percentage || 0,
            category: product.category,
            image: null, // Reset image input, keep existing if not changed
            is_active: product.is_active !== undefined ? product.is_active : true,
            is_available: product.is_available !== undefined ? product.is_available : true,
            max_toppings: product.max_toppings || 0,
            contifico_id: product.contifico_id || ''
        });
        setIsModalOpen(true);
    };

    const handleDeleteProduct = async (id) => {
        if (window.confirm('¿Estás seguro de eliminar este producto? (Se archivará para no afectar reportes históricos)')) {
            try {
                // "Soft Delete": Desactivar en lugar de borrar físicamente para mantener integridad referencial
                const formData = new FormData();
                formData.append('is_active', 'false');
                formData.append('is_available', 'false');

                // Backend usa lookup_field = 'pk' (UUID), así que usamos el ID.
                await api.patch(`/api/menu/products/${id}/`, formData, {
                    baseURL: process.env.REACT_APP_HELADERIA_SERVICE,
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                fetchProducts();
            } catch (err) {
                console.error('Error deleting product:', err);
                const msg = err.response?.data?.detail || 'Error al eliminar el producto';
                alert(`Error: ${msg}`);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const formData = new FormData();
        formData.append('name', newProduct.name);
        formData.append('description', newProduct.description);
        formData.append('price', newProduct.price);
        formData.append('tax_percentage', newProduct.tax_percentage || 0);
        formData.append('category', newProduct.category);
        formData.append('is_active', newProduct.is_active ? 'true' : 'false');
        formData.append('is_available', newProduct.is_available ? 'true' : 'false');
        formData.append('max_toppings', newProduct.max_toppings || 0);
        formData.append('contifico_id', newProduct.contifico_id || '');
        if (newProduct.image instanceof File) {
            formData.append('image', newProduct.image);
        }

        // Solo generar SLUG si es un producto nuevo.
        if (!editingProduct) {
            const slug = newProduct.name.toLowerCase()
                .replace(/ /g, '-')
                .replace(/[^\w-]+/g, '');
            formData.append('slug', slug);
        }

        try {
            if (editingProduct) {
                // Usar ID para el PATCH ya que el backend espera PK
                await api.patch(`/api/menu/products/${editingProduct.id}/`, formData, {
                    baseURL: process.env.REACT_APP_HELADERIA_SERVICE,
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            } else {
                await api.post('/api/menu/products/', formData, {
                    baseURL: process.env.REACT_APP_HELADERIA_SERVICE,
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
            }
            setIsModalOpen(false);
            setNewProduct({ name: '', description: '', price: '', tax_percentage: 0, category: '', image: null, is_active: true, is_available: true, max_toppings: 0, contifico_id: '' });
            setEditingProduct(null);
            fetchProducts();
        } catch (err) {
            console.error('Error saving product:', err);
            const msg = err.response?.data?.detail || err.response?.data?.name || 'Error al guardar el producto. Verifique los datos.';
            alert(`Error: ${msg}`);
        }
    };

    const filteredProducts = products.filter(product =>
        (product.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.category_name || product.category || '').toString().toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="page-container">
            <div className="page-header">
                <h2>Inventario (Menú)</h2>
            </div>

            {/* Tabs de Navegación */}
            <div className="tabs" style={{ marginBottom: '20px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '10px' }}>
                <button
                    className={`btn ${activeTab === 'products' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveTab('products')}
                >
                    Productos
                </button>
                <button
                    className={`btn ${activeTab === 'categories' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveTab('categories')}
                >
                    Categorías
                </button>
                <button
                    className={`btn ${activeTab === 'combos' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveTab('combos')}
                >
                    Combos
                </button>
                <button
                    className={`btn ${activeTab === 'extras' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveTab('extras')}
                >
                    Extras
                </button>
                <button
                    className={`btn ${activeTab === 'sizes' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveTab('sizes')}
                >
                    Tamaños
                </button>

                {isAdmin && (
                    <button
                        className={`btn ${activeTab === 'insumos' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setActiveTab('insumos')}
                    >
                        Insumos
                    </button>
                )}
            </div>

            {/* Contenido de Pestañas */}
            {activeTab === 'categories' && <Categorias isAdmin={isAdmin} />}
            {activeTab === 'extras' && <Extras isAdmin={isAdmin} />}
            {activeTab === 'combos' && <Combos isAdmin={isAdmin} />}
            {activeTab === 'sizes' && <Tamanos isAdmin={isAdmin} />}


            {/* Contenido de Productos */}
            {
                activeTab === 'products' && (
                    <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flex: 1 }}>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => setIsProductMovementsModalOpen(true)}
                                >
                                    Movimientos
                                </button>
                                <input 
                                    type="text" 
                                    placeholder="Buscar producto por nombre o categoría..." 
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="form-control"
                                    style={{ maxWidth: '400px' }}
                                />
                            </div>
                            {isAdmin && (
                                <button className="btn btn-primary" onClick={() => {
                                    setEditingProduct(null);
                                    setNewProduct({ name: '', description: '', price: '', tax_percentage: 0, category: '', image: null, is_active: true, is_available: true, max_toppings: 0 });
                                    setIsModalOpen(true);
                                }}>
                                    Nuevo Producto
                                </button>
                            )}
                        </div>

                        {loading ? <div>Cargando inventario...</div> : error ? <div className="alert alert-error">{error}</div> : (
                            <div className="table-responsive">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Imagen</th>
                                            <th>Nombre</th>
                                            <th>Categoría</th>
                                            <th>Precio Base</th>
                                            <th>% IVA</th>
                                            <th>Precio Final</th>
                                            <th>Disponible</th>
                                            <th>ID Contífico</th>
                                            {isAdmin && <th>Acciones</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredProducts.length === 0 ? (
                                            <tr><td colSpan="9">No hay productos que coincidan con la búsqueda</td></tr>
                                        ) : (
                                            filteredProducts.map(product => (
                                                <tr key={product.id}>
                                                    <td>
                                                        {product.image ? (
                                                            <img
                                                                src={product.image.startsWith('http') ? product.image : `${process.env.REACT_APP_HELADERIA_SERVICE}${product.image}`}
                                                                alt={product.name}
                                                                style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '5px' }}
                                                            />
                                                        ) : (
                                                            <span style={{ color: '#888' }}>Sin imagen</span>
                                                        )}
                                                    </td>
                                                    <td>{product.name}</td>
                                                    <td>{product.category_name || product.category}</td>
                                                    <td>
                                                        ${(parseFloat(product.price) / (1 + parseFloat(product.tax_percentage || 0) / 100)).toFixed(2)}
                                                    </td>
                                                    <td>{product.tax_percentage ? `${parseFloat(product.tax_percentage).toFixed(0)}%` : '0%'}</td>
                                                    <td>
                                                        <span style={{ fontWeight: 'bold', color: 'var(--primary-color)' }}>
                                                            ${parseFloat(product.price).toFixed(2)}
                                                        </span>
                                                    </td>
                                                    <td>{product.is_available ? 'Sí' : 'No'}</td>
                                                    <td>{product.contifico_id ? <span style={{color: '#10b981', fontWeight: 'bold'}}>{product.contifico_id}</span> : <span style={{ color: '#888' }}>No vinculado</span>}</td>
                                                    {isAdmin && (
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-outline"
                                                                onClick={() => handleEditProduct(product)}
                                                                style={{ marginRight: '5px' }}
                                                            >
                                                                ✏️
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() => handleDeleteProduct(product.id)}
                                                            >
                                                                🗑️
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}


                        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingProduct ? "Editar Producto" : "Nuevo Producto"}>
                            <form onSubmit={handleSubmit}>
                                <div className="form-group">
                                    <label>Nombre</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={newProduct.name}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Descripción</label>
                                    <textarea
                                        name="description"
                                        value={newProduct.description}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Precio Final (Inc. IVA)</label>
                                    <input
                                        type="number"
                                        name="price"
                                        value={newProduct.price}
                                        onChange={handleInputChange}
                                        step="0.01"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>% IVA (Ej: 15 o 0)</label>
                                    <input
                                        type="number"
                                        name="tax_percentage"
                                        value={newProduct.tax_percentage}
                                        onChange={handleInputChange}
                                        step="0.01"
                                        min="0"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Categoría</label>
                                    <select
                                        className="form-control"
                                        name="category"
                                        value={newProduct.category}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Seleccione una categoría</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="is_active"
                                            checked={newProduct.is_active}
                                            onChange={handleInputChange}
                                            style={{ marginRight: '8px' }}
                                        />
                                        Activo
                                    </label>
                                </div>
                                <div className="form-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            name="is_available"
                                            checked={newProduct.is_available}
                                            onChange={handleInputChange}
                                            style={{ marginRight: '8px' }}
                                        />
                                        Disponible
                                    </label>
                                </div>
                                <div className="form-group">
                                    <label>Máximo Toppings (0 para ninguno)</label>
                                    <input
                                        type="number"
                                        name="max_toppings"
                                        value={newProduct.max_toppings}
                                        onChange={handleInputChange}
                                        min="0"
                                        className="form-control"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>ID Contífico</label>
                                    <input
                                        type="text"
                                        name="contifico_id"
                                        value={newProduct.contifico_id}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        placeholder="Dejar en blanco para generar nuevo al sincronizar"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Imagen</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        required={!editingProduct}
                                    />
                                </div>
                                <div className="form-actions">
                                    <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>Cancelar</button>
                                    <button type="submit" className="btn btn-primary">Guardar</button>
                                </div>
                            </form>
                        </Modal>
                        <Modal
                           isOpen={isProductMovementsModalOpen}
                           onClose={() => setIsProductMovementsModalOpen(false)}
                           title="Movimientos de Inventario"
                           style={{ maxWidth: '900px' }}
                         >
                           <MovimientosInventario />
                         </Modal>
                    </>
                )
            }
        </div >
    );
};

export default Inventario;

const styles = `
    .page-container {
        padding: 20px;
        max-width: 1600px;
        margin: 0 auto;
    }
    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
    }
    .page-header h2 {
        margin: 0;
        color: #1f2937;
    }
    
    /* Tabs */
    .tabs {
        display: flex;
        gap: 10px;
        border-bottom: 1px solid #e2e8f0;
        margin-bottom: 20px;
        overflow-x: auto;
        padding-bottom: 5px;
        white-space: nowrap;
        -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
    }
    .tabs::-webkit-scrollbar {
        height: 4px;
    }
    .tabs::-webkit-scrollbar-thumb {
        background-color: #ccc;
        border-radius: 4px;
    }

    .btn {
        padding: 0.5rem 1rem;
        border-radius: 0.375rem;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
        border: 1px solid transparent;
    }
    .btn-primary {
        background-color: var(--primary-color);
        color: white;
        border-color: var(--primary-color);
    }
    .btn-primary:hover {
        background-color: #2563eb;
    }
    .btn-secondary {
        background-color: var(--sidebar-bg);
        color: #374151;
        border-color: #d1d5db;
    }
    .btn-secondary:hover {
        background-color: #e5e7eb;
    }
    .btn-danger {
        background-color: #fee2e2;
        color: #dc2626;
        border-color: #fca5a5;
    }
    .btn-danger:hover {
        background-color: #fecaca;
    }

    /* Table */
    .table-responsive {
        width: 100%;
        overflow-x: auto;
        overflow-y: auto; /* Scroll vertical */
        max-height: 70vh; /* Altura máxima para permitir scroll */
        -webkit-overflow-scrolling: touch;
        border-radius: 0.5rem;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        border: 1px solid #e5e7eb;
        position: relative; /* Para sticky header */
    }
    .table {
        width: 100%;
        border-collapse: collapse;
        background-color: white;
        min-width: 600px; 
    }
    .table th, .table td {
        padding: 0.75rem 1rem;
        text-align: left;
        border-bottom: 1px solid #e5e7eb;
    }
    .table th {
        background-color: var(--sidebar-bg);
        font-weight: 600;
        color: #374151;
        font-size: 0.875rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        position: sticky; /* Header fijo */
        top: 0;
        z-index: 10;
        box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    }
    .table td {
        font-size: 0.875rem;
        color: #4b5563;
    }
    .table tr:last-child td {
        border-bottom: none;
    }

    /* Form */
    .form-group {
        margin-bottom: 1rem;
    }
    .form-group label {
        display: block;
        margin-bottom: 0.5rem;
        font-weight: 500;
        color: #374151;
    }
    .form-control, input[type="text"], input[type="number"], textarea, select {
        width: 100%;
        padding: 0.5rem;
        border: 1px solid #d1d5db;
        border-radius: 0.375rem;
        font-size: 1rem;
    }
    .form-actions {
        display: flex;
        justify-content: flex-end;
        gap: 1rem;
        margin-top: 1.5rem;
    }

    /* Alerts */
    .alert {
        padding: 1rem;
        margin-bottom: 1rem;
        border-radius: 0.375rem;
    }
    .alert-error {
        background-color: #fee2e2;
        color: #991b1b;
        border: 1px solid #fecaca;
    }

    /* Responsive Media Queries */
    @media (max-width: 768px) {
        .page-container {
            padding: 10px;
        }
        .page-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
        }
        .page-header h2 {
            font-size: 1.5rem;
        }
        .tabs {
            padding-bottom: 10px;
        }
        .btn {
            padding: 0.4rem 0.8rem;
            font-size: 0.9rem;
        }
    }
`;

// Inject styles
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

